import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import TextField from '@material-ui/core/TextField';
import { Caption2 } from '../../components/Typography';
import { withStyles } from '@material-ui/core/styles';
import { Formik } from 'formik';
import FormGrid from '../../components/Form/FormGrid';
import appConfigStyle from '../../../app-config';
import * as loConfig from '../../../app-config';
import {
  PrimaryButton,
  SecondaryButton,
  ButtonRow,
} from '../../components/Buttons';
import WarningBox from './WarningBox';
import PasswordField from '../../layout/components/PasswordField';
import ReCAPTCHA from 'react-google-recaptcha';
import { getReCaptchaSiteKey } from '../../utils/getReCaptcha';

const Wrapper = styled.div`
  display: grid;
  grid-gap: 14px;
  margin-top: -25px;
`;

const FormLayout = styled(FormGrid)`
  padding-top: 40px;
`;

const styles = (theme) => ({
  input: {
    fontFamily: theme.typography.fontFamily.main,
  },
});

const ForgotLoginParams = styled(Caption2)`
  color: ${(props) => props.theme.clickableTextColor};
  cursor: pointer;
  margin-top: -13px;
  margin-left: 5px;
  &:hover {
    text-decoration: underline;
    color: ${(props) => props.theme.clickableTextColorHover};
    transition: all 0.25s ease;
  }
`;

const isEmail = new RegExp(
  '^[_A-Za-z0-9-\\+]+(\\.[_A-Za-z0-9-]+)*@' +
    '[A-Za-z0-9-]+(\\.[A-Za-z0-9]+)*(\\.[A-Za-z]{2,})$'
);

const LoginForm = ({
  classes,
  login,
  handleChangeScreen,
  resetAuthReducer,
  resetCheckedEmail,
  setEmailOrPhoneField,
  loginFailureError,
  appConfig,
  setShowGuestPersonalDetailsPage,
  updateCartScreen,
  setAuthenticationModalIsVisible,
  cartScreen,
  requestingLogin,
  setSelectedCategoryId,
  setEnteredPhoneNumber,
  setEnteredEmail,
  setLoginObject,
  setScreenToShow,
}) => {
  const { is_captcha_enabled } = appConfig || {};

  let passwordLessMethods;
  let passwordLessPlaceholder;

  if (loConfig.passwordLessType === 'phone') {
    passwordLessPlaceholder = 'Phone Number';
    passwordLessMethods = 'phone';
  } else if (loConfig.passwordLessType === 'both') {
    passwordLessPlaceholder = 'Phone Number or Email';
    passwordLessMethods = 'both';
  } else {
    passwordLessPlaceholder = 'Email';
    passwordLessMethods = 'email';
  }

  const reCaptchaSiteKey = getReCaptchaSiteKey();
  const recaptchaRef = React.useRef();
  const isPasswordLess = appConfig?.is_password_less;
  const [latitude, setLatitude] = useState();
  const [longitude, setLongitude] = useState();
  useEffect(() => {
    navigator.geolocation.getCurrentPosition(function(position) {
      setLatitude(position.coords.latitude);
      setLongitude(position.coords.longitude);
    });
    return function cleanup() {
      setEmailOrPhoneField('');
      resetAuthReducer();
      resetCheckedEmail();
    };
  }, []);
  const [showPassword, setShowPassword] = useState(true);
  const resetRecaptchaTokenCallback = () => {
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
  };
  const handleSub = async (values) => {
    let loginProps = { source: appConfigStyle.appSource };
    if (!isPasswordLess) {
      loginProps = {
        ...loginProps,
        latitude: latitude || 0,
        longitude: longitude || 0,
        email: values.email,
        password: values.password,
      };
      if (is_captcha_enabled) {
        const token = await recaptchaRef.current?.executeAsync({
          action: 'LOGIN',
        });
        if (token) {
          loginProps.site_key = reCaptchaSiteKey;
          loginProps.captcha_token = token;
        }
      }
      login(
        loginProps,
        setSelectedCategoryId,
        null,
        resetRecaptchaTokenCallback
      );
    } else {
      if (values.email.includes('@')) {
        loginProps.email = values.email;
        setEnteredEmail(values.email);
      } else {
        loginProps.phone_number = values.email;
        setEnteredPhoneNumber(values.email);
      }
      if (is_captcha_enabled) {
        const token = await recaptchaRef.current?.executeAsync({
          action: 'LOGIN',
        });
        if (token) {
          loginProps.site_key = reCaptchaSiteKey;
          loginProps.captcha_token = token;
        }
      }
      login(
        loginProps,
        setSelectedCategoryId,
        null,
        resetRecaptchaTokenCallback
      );
    }
    setLoginObject(loginProps);
  };
  return (
    <Wrapper>
      {loginFailureError && (
        <WarningBox
          message={
            loginFailureError === 'Wrong username/password' ||
            loginFailureError === 'Account does not exist'
              ? 'Wrong email or password'
              : loginFailureError
          }
        />
      )}
      <Formik
        initialValues={{ email: '', password: '' }}
        validate={(values) => {
          let errors = {};

          if (isPasswordLess) {
            if (!values.email) {
              errors.email = 'Required';
            } else if (
              passwordLessMethods === 'email' &&
              !isEmail.test(values.email)
            ) {
              errors.email = 'Please enter a valid email address';
            } else if (
              passwordLessMethods === 'phone' &&
              (values.email.match(/[^$,.\d]/) || values.email.length < 10)
            ) {
              errors.email = 'Please enter a valid phone number';
            } else if (
              passwordLessMethods === 'both' &&
              ((!isEmail.test(values.email) &&
                !values.email.includes('@') &&
                values.email.match(/[^$,.\d]/)) ||
                (!values.email.includes('@') && values.email.length < 10))
            ) {
              errors.email = 'Enter a valid email or phone number';
            }
          } else {
            if (!values.email) {
              errors.email = 'Required';
            } else if (
              isNaN(parseFloat(values.email)) &&
              !isEmail.test(values.email)
            ) {
              errors.email = 'Please put a valid email format';
            } else if (
              (!values.email.includes('@') && values.email.match(/[^$,.\d]/)) ||
              (!values.email.includes('@') && values.email.length < 10)
            ) {
              errors.email = 'Please put a valid phone number';
            }
            if (!values.password) {
              errors.password = 'Required';
            }
          }
          return errors;
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
        }) => (
          <form onSubmit={handleSubmit}>
            <FormLayout>
              <TextField
                id='EmailForm-Username'
                label={
                  appConfig.is_password_less
                    ? `${passwordLessPlaceholder}`
                    : 'Email'
                }
                name='email'
                autoComplete='username'
                value={values.email}
                onChange={handleChange}
                required
                onBlur={handleBlur}
                error={touched.email && errors.email ? true : false}
                helperText={touched.email && errors.email}
                InputLabelProps={{
                  className: classes.input,
                  required: false,
                  style: { color: '#a9a9a9' },
                }}
              />
              {!isPasswordLess && (
                <PasswordField
                  id='RegistrationForm-PasswordField'
                  label='Password'
                  name='password'
                  autoComplete='off'
                  value={values.password}
                  showPassword={showPassword}
                  setShowPassword={setShowPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  classes={classes}
                  helperText={touched.password && errors.password}
                  error={touched.password && errors.password ? true : false}
                />
              )}
              {!isPasswordLess && (
                <ForgotLoginParams
                  id='LoginForm-ForgotPassword'
                  onClick={() => handleChangeScreen(2)}
                >
                  Forgot Password?
                </ForgotLoginParams>
              )}
              {is_captcha_enabled && (
                <ReCAPTCHA
                  ref={recaptchaRef}
                  size='invisible'
                  sitekey={reCaptchaSiteKey}
                />
              )}

              <ButtonRow justifyContent='center' margin='25px 0 15px 0' row>
                <PrimaryButton
                  id='EmailForm-Submit'
                  type='submit'
                  disabled={
                    !values.email ||
                    (!isPasswordLess && !values.password) ||
                    (touched.email && errors.email)
                  }
                  buttonWidth='240px'
                  buttonHeight='56px'
                  buttonBorderRadius='10px'
                  fontWeight='600'
                  onClick={() => handleSub(values)}
                >
                  Login
                </PrimaryButton>
              </ButtonRow>
            </FormLayout>
          </form>
        )}
      </Formik>
      {appConfig && appConfig.guest_checkout_enabled ? (
        <ButtonRow justifyContent='center'>
          <SecondaryButton
            id='Continue-As-Guest'
            buttonWidth='240px'
            buttonHeight='56px'
            buttonBorderRadius='10px'
            fontWeight='600'
            onClick={() => {
              if (appConfig.appSource === 'customer_info') {
                updateCartScreen('customer-info');
                setAuthenticationModalIsVisible(false);
              } else if (cartScreen === 'review') {
                setShowGuestPersonalDetailsPage(true);
                updateCartScreen('guest-order');
                setScreenToShow('personal-details');
              } else {
                setAuthenticationModalIsVisible(false);
              }
            }}
          >
            Continue as Guest
          </SecondaryButton>
        </ButtonRow>
      ) : null}
    </Wrapper>
  );
};

export default withStyles(styles)(LoginForm);
